/* eslint-disable eqeqeq */

import qs from 'qs';

import { api } from '@ezugi/utils';

import { GAME_ID_TO_SLUGS_MAP, GAME_SLUGS } from '../../../constants/game';
import { RootState } from '../../../store/types';
import { API_PREFIX } from '../../../util/apiPrefix';
import { formatSelector } from '../../selectors';
import { localeSelector } from '../../selectors/locale';

const VITE_API_URL = import.meta.env.VITE_API_URL ?? '';
const VITE_API_FETCH_DETAILED_HISTORY = import.meta.env.VITE_API_FETCH_DETAILED_HISTORY;

const DAY_HISTORY_API = '/server/services/history/index.php';
const RECORDS_HISTORY_API = '/server/services/history/records.php';

const FETCH_PLAYER_HISTORY_URL = `${VITE_API_URL}${API_PREFIX}${DAY_HISTORY_API}`;
const FETCH_DAY_RECORDS_HISTORY_URL = `${VITE_API_URL}${API_PREFIX}${RECORDS_HISTORY_API}`;
const FETCH_DETAILED_HISTORY_URL = `${VITE_API_URL}${API_PREFIX}${VITE_API_FETCH_DETAILED_HISTORY}`;

const parsersMap = {
  [GAME_SLUGS.BJ]: () => import('./parsers/blackjack'),
  [GAME_SLUGS.BAC]: () => import('./parsers/baccarat'),
  [GAME_SLUGS.ROU]: () => import('./parsers/roulettes/roulette'),
  [GAME_SLUGS.TZROU]: () => import('./parsers/roulettes/triplezeroroulette'),
  [GAME_SLUGS.REVROU]: () => import('./parsers/roulettes/rouletterevolution'),
  [GAME_SLUGS.ANDAR]: () => import('./parsers/andarbahar'),
  [GAME_SLUGS.UAB]: () => import('./parsers/andarbahar'),
  [GAME_SLUGS.OTTAB]: () => import('./parsers/ottandarbahar'),
  [GAME_SLUGS.TCP]: () => import('./parsers/threecardpoker'),
  [GAME_SLUGS.CH]: () => import('./parsers/casinoholdem'),
  [GAME_SLUGS.BOTP]: () => import('./parsers/betonteenpatti'),
  [GAME_SLUGS.ODTP]: () => import('./parsers/onedayteenpatti'),
  [GAME_SLUGS.ODTP_BL]: () => import('./parsers/onedayteenpattibl'),
  [GAME_SLUGS.L7]: () => import('./parsers/lucky7'),
  [GAME_SLUGS.TTC]: () => import('./parsers/thirtytwocards'),
  [GAME_SLUGS.SICBO]: () => import('./parsers/sicbo'),
  [GAME_SLUGS.RP]: () => import('./parsers/russianpoker'),
  [GAME_SLUGS.UR]: () => import('./parsers/roulettes/ultimateroulette'),
  [GAME_SLUGS.XD]: () => import('./parsers/xocdia'),
  [GAME_SLUGS.MAT]: () => import('./parsers/matka'),
  [GAME_SLUGS.MR]: () => import('./parsers/marblerace'),
  [GAME_SLUGS.BW]: () => import('./parsers/bonuswheel'),
  // TODO: will be implemented in a separate task
  [GAME_SLUGS.UD]: () => ({ default: () => null }),
};

type QueryParam = {
  identifier: string;
  time_period?: number;
  current_time: number;
};

type BankRoll = {
  Bankroll: string;
};

type PlayerHistoryResponse = {
  result: unknown;
};

export const getPlayerHistory = (params: QueryParam) => api.get<PlayerHistoryResponse>(
    `${FETCH_PLAYER_HISTORY_URL}?${qs.stringify(params)}`
);

type DayRoundHistoryResponse = {
  result: unknown;
};

export const getDayRoundsHistory = (
  params: QueryParam & { day: string; oldest_round_id?: string }
) => api.get<DayRoundHistoryResponse>(
  `${FETCH_DAY_RECORDS_HISTORY_URL}?${qs.stringify(params)}`
);

type RoundDetailsResponse = {
  result: any[];
};

export const getRoundDetails = async (params: QueryParam, state: RootState) => {
  const res = await api.get<RoundDetailsResponse>(
    `${FETCH_DETAILED_HISTORY_URL}?${qs.stringify(params)}`,
  );

  const { result } = res || {};

  if (!result || result.length === 0) {
    throw Error('No result');
  }

  const lastTransaction = result[0].ActionID != 2
    ? result[0]
    : result
      .filter(({ ActionID }: { ActionID: number }) => (ActionID == 2))
      .sort((a: BankRoll, b: BankRoll) => (+a.Bankroll > +b.Bankroll ? -1 : 1))[0];

  const {
    GameTypeID: gameId,
    Bankroll: endBalance,
    RoundDateTime: endDateTime,
    SessionCurrency: currency,
  }: {
    GameTypeID: keyof typeof GAME_ID_TO_SLUGS_MAP;
    Bankroll: number,
    RoundDateTime: number,
    SessionCurrency: string,
  } = (lastTransaction || result[0]);
  const { Bankroll: startBalance, Bet: startBet, RoundDateTime: startDateTime } = result[result.length - 1];
  const gameSlug = GAME_ID_TO_SLUGS_MAP[gameId];
  const { default: parserFn = (x: any) => x } = await parsersMap[gameSlug]();
  const locale = localeSelector(state);
  const { dateFormat, ...amountFormat } = formatSelector(state) ?? {};
  const parsedData = parserFn({ result, dateFormat, amountFormat, locale });

  return {
    currency,
    startBalance: +startBalance + +startBet,
    endBalance: +endBalance,
    startDateTime,
    endDateTime,
    ...parsedData,
  };
};
